import { createFeatureSelector, createSelector } from '@ngrx/store';
import { bookmarkProblemListFeatureKey, State } from '../reducers/bookmark-problem-list.reducer';
import {
  getStaticCommonData,
  getStaticEnglishData,
  getStaticMathData,
  getStaticNationalLanguageData,
  getStaticPhysicsData,
  getStaticChemistryData,
  getStaticBiologyData,
  getStaticJapaneseHistoryData,
  getStaticWorldHistoryData,
  getStaticGeographyData,
  getStaticPoliticalEconomyData,
  getStaticSpecialAdmissionsData
} from './static-data.selectors';
import {
  translateEnglishProblems,
  translateHistoryProblems,
  translateNationalLanguageProblems,
  translateScienceProblems
} from './search.selectors';
import { EnglishProblem, NationalLanguageProblem, ScienceProblem } from '../models/problem';
import { SubjectId } from '../resources/config';

const selectBookmarkProblemListState = createFeatureSelector<State>(bookmarkProblemListFeatureKey);

export const getBookmarkProblemLists = createSelector(selectBookmarkProblemListState, state => state.bookmarkProblemLists);
export const getSharedBookmarkProblemLists = createSelector(selectBookmarkProblemListState, state => state.sharedBookmarkProblemLists);
export const getCreateBookmarkProblemListResult = createSelector(
  selectBookmarkProblemListState,
  state => state.createBookmarkProblemListResult
);
export const getUpdateBookmarkProblemListResult = createSelector(
  selectBookmarkProblemListState,
  state => state.updateBookmarkProblemListResult
);
export const getCopyBookmarkProblemListResult = createSelector(
  selectBookmarkProblemListState,
  state => state.copyBookmarkProblemListResult
);
export const getDeleteBookmarkProblemListResult = createSelector(
  selectBookmarkProblemListState,
  state => state.deleteBookmarkProblemListResult
);

export const getAddProblemToBookmarkProblemListResult = createSelector(
  selectBookmarkProblemListState,
  state => state.addProblemToBookmarkProblemListResult
);
export const getDeleteProblemFromBookmarkProblemListResult = createSelector(
  selectBookmarkProblemListState,
  state => state.deleteProblemFromBookmarkProblemListResult
);

export const getMatchedBookmarkProblemListProblemIds = createSelector(
  selectBookmarkProblemListState,
  state => state.matchedBookmarkProblemListProblemIds
);
export const getBookmarkProblemListProblemIdsSearching = createSelector(
  selectBookmarkProblemListState,
  state => state.isBookmarkProblemListProblemIdsSearching
);

export const getFirstProblemIdFromMatchedBookmarkProblemListProblemIds = createSelector(
  getMatchedBookmarkProblemListProblemIds,
  (problemIds: string[] | null): string | null => {
    if (problemIds == null) return null;
    return [...problemIds].shift() || '';
  }
);

export const getLastProblemIdFromMatchedBookmarkProblemListProblemIds = createSelector(
  getMatchedBookmarkProblemListProblemIds,
  (problemIds: string[] | null): string | null => {
    if (problemIds == null) return null;
    return [...problemIds].pop() || '';
  }
);

export const getPreviousBookmarkProblemListProblemId = (currentProblemId: string) =>
  createSelector(getMatchedBookmarkProblemListProblemIds, (problemIds: string[] | null): string | null => {
    if (problemIds == null) return null;
    const prevIdIndex = problemIds.findIndex(it => it === currentProblemId) - 1;
    if (prevIdIndex < 0) return '';
    return problemIds[prevIdIndex];
  });

export const getNextBookmarkProblemListProblemId = (currentProblemId: string) =>
  createSelector(getMatchedBookmarkProblemListProblemIds, (problemIds: string[] | null): string | null => {
    console.log('a');
    console.log(problemIds);
    if (problemIds == null) return null;
    const nextIdIndex = problemIds.findIndex(it => it === currentProblemId) + 1;
    console.log('b');
    console.log(nextIdIndex);
    if (nextIdIndex >= problemIds.length) return '';
    return problemIds[nextIdIndex];
  });

export const getBookmarkProblemListProblemsSearching = createSelector(
  selectBookmarkProblemListState,
  state => state.bookmarkProblemListProblemsSearching
);
export const getBookmarkProblemListProblems = createSelector(selectBookmarkProblemListState, state => state.bookmarkProblemListProblems);

export const getReadableEnglishBookmarkProblemListProblems = createSelector(
  getStaticCommonData,
  getStaticEnglishData,
  getBookmarkProblemListProblems,
  (staticCommonData, staticEnglishData, bookmarkProblemListProblems) =>
    translateEnglishProblems(staticCommonData, staticEnglishData, bookmarkProblemListProblems as EnglishProblem[])
);

export const getReadableScienceBookmarkProblemListProblemsBySubject = (subjectId: string) =>
  createSelector(
    getStaticCommonData,
    getStaticMathData,
    getStaticPhysicsData,
    getStaticChemistryData,
    getStaticBiologyData,
    getStaticGeographyData,
    getStaticPoliticalEconomyData,
    getStaticSpecialAdmissionsData,
    getBookmarkProblemListProblems,
    (
      staticCommonData,
      staticMathData,
      staticPhysicsData,
      staticChemistryData,
      staticBiologyData,
      staticGeographyData,
      staticPoliticalEconomyData,
      staticSpecialAdmissionsData,
      bookmarkProblemListProblems
    ) => {
      const staticSubjectData =
        subjectId === SubjectId.MATH
          ? staticMathData
          : subjectId === SubjectId.PHYSICS
          ? staticPhysicsData
          : subjectId === SubjectId.CHEMISTRY
          ? staticChemistryData
          : subjectId === SubjectId.BIOLOGY
          ? staticBiologyData
          : subjectId === SubjectId.GEOGRAPHY
          ? staticGeographyData
          : subjectId === SubjectId.POLITICAL_ECONOMY
          ? staticPoliticalEconomyData
          : subjectId === SubjectId.SPECIAL_ADMISSIONS
          ? staticSpecialAdmissionsData
          : null;

      return translateScienceProblems(staticCommonData, staticSubjectData, bookmarkProblemListProblems as ScienceProblem[]);
    }
  );

export const getReadableNationalLanguageBookmarkProblemListProblems = createSelector(
  getStaticCommonData,
  getStaticNationalLanguageData,
  getBookmarkProblemListProblems,
  (staticCommonData, staticNationalLanguageData, bookmarkProblemListProblems) =>
    translateNationalLanguageProblems(
      staticCommonData,
      staticNationalLanguageData,
      bookmarkProblemListProblems as NationalLanguageProblem[]
    )
);

export const getReadableHistoryBookmarkProblemListProblemsBySubject = (subjectId: string) =>
  createSelector(
    getStaticCommonData,
    getStaticJapaneseHistoryData,
    getStaticWorldHistoryData,
    getBookmarkProblemListProblems,
    (staticCommonData, staticJapaneseHistoryData, staticWorldHistoryData, bookmarkProblemListProblems) => {
      const staticSubjectData =
        subjectId === SubjectId.JAPANESE_HISTORY
          ? staticJapaneseHistoryData
          : subjectId === SubjectId.WORLD_HISTORY
          ? staticWorldHistoryData
          : null;
      return translateHistoryProblems(staticCommonData, staticSubjectData, bookmarkProblemListProblems as NationalLanguageProblem[]);
    }
  );

export const getReorderBookmarkProblemListProblemsResult = createSelector(
  selectBookmarkProblemListState,
  state => state.reorderBookmarProblemListProblemsResult
);
